import { containsMath, resizeProperty } from '@/helpers';
import { ActionIcon, Anchor, Button, Code, Text, UnstyledButton } from '@mantine/core';
import KatexRenderer from '../RichTextEditor/KatexRenderer';
import { modals } from '@mantine/modals';
import { WebsiteSimple } from '../Website/WebsiteSimple';
import { IconExternalLink } from '@tabler/icons-react';

type Style = {
  color?: string;
  backgroundColor?: string;
};

export function TextFragment({ fragment, formValues, containerWidth }) {
  const {
    content,
    bold,
    italic,
    underline,
    strikethrough,
    superscript,
    subscript,
    color,
    backgroundColor,
    linkUrl,
    code,
  } = fragment;

  let textElement = undefined;

  const initialContent = () => {
    if (superscript) return <sup>{content}</sup>;
    if (subscript) return <sub>{content}</sub>;

    return content;
  };

  if (content === '\n') {
    return <br />;
  }

  if (linkUrl) {
    textElement = (
      <>
        <UnstyledButton
          pr="sm"
          component="span"
          c={formValues?.all_headings_font_color}
          fw={bold ? 700 : ''}
          fz={resizeProperty(containerWidth, formValues.body_font_size)}
          fs={italic ? 'italic' : ''}
          td={`underline ${strikethrough ? 'line-through' : ''}`.trim()}
          onClick={() =>
            modals.open({
              children: <WebsiteSimple src={linkUrl} closeModals={modals.closeAll} />,
              fullScreen: true,
              withCloseButton: false,
            })
          }
        >
          {initialContent()}
        </UnstyledButton>
        <ActionIcon
          variant="subtle"
          aria-label="Settings"
          component="a"
          href={linkUrl}
          target="_blank"
          color={formValues?.all_headings_font_color}
        >
          <IconExternalLink style={{ width: '100%', height: '100%' }} stroke={2} />
        </ActionIcon>
      </>
    );
  } else if (code) {
    return (
      <Code
        fw={formValues.body_font_weight}
        fz={resizeProperty(containerWidth, formValues.body_font_size)}
        fs={formValues.body_italic ? 'italic' : 'normal'}
      >
        {initialContent()}
      </Code>
    );
  } else if (containsMath(content)) {
    return (
      <KatexRenderer
        equation={content.replace(/^\$|\$$/g, '')}
        inline={true}
        onDoubleClick={() => {}}
        style={{
          fontSize: resizeProperty(containerWidth, formValues.body_font_size),
          color: formValues.body_font_color,
        }}
      />
    );
  } else {
    let style: Style = {};
    if (color) style.color = color;
    if (backgroundColor) style.backgroundColor = backgroundColor;

    textElement = (
      <Text
        fw={bold ? 700 : ''}
        fs={italic ? 'italic' : ''}
        td={`${underline ? 'underline' : ''} ${strikethrough ? 'line-through' : ''}`.trim()}
        style={style}
        span
        inherit
      >
        {initialContent()}
      </Text>
    );
  }

  return textElement;
}
